import {styled} from '@linaria/react';
import React, {RefObject} from 'react';
import {
  mediaDesktopAndUp,
  mediaExtraLargeScreens,
  mediaLargeScreens,
  mediaMinL,
} from '../../../constants/customMedia';
import {RowSection, RowSectionProps} from './RowSection';

export type SplitType =
  | '50:50'
  | '65:35'
  | '20:80'
  | '15:85'
  | '30:70'
  | '45:25'
  | '60:25'
  | '60:40';

interface SplitViewProps extends RowSectionProps, ComponentSplit {
  contentStart: React.ReactNode;
  contentEnd: React.ReactNode;
  refStart?: React.RefObject<HTMLDivElement | null>;
  refEnd?: React.RefObject<HTMLDivElement | null>;
  className?: string;
  ref?: React.Ref<HTMLElement>;
}

interface ComponentSplit {
  split?: SplitType;
  splitMedium?: SplitType;
  // if this prop is passed as true, a ratio of 0:!00 will be applied to the sections
  splitLarge?: SplitType;
  splitExtraLarge?: SplitType;
  disableSplit?: boolean;
  reducePadding?: boolean;
}

export const SplitView = ({
  dataTestId,
  contentStart,
  contentEnd,
  refStart,
  refEnd,
  split,
  splitMedium,
  splitLarge,
  splitExtraLarge,
  reducePadding,
  disableSplit,
  ref,
  ...props
}: SplitViewProps) => {
  return (
    <StyledRowSection
      data-testid={dataTestId || 'splitView'}
      data-disable-split={Boolean(disableSplit)}
      data-reduce-padding={Boolean(reducePadding)}
      ref={ref as RefObject<HTMLElement | null>}
      disableSplit={disableSplit}
      {...props}
    >
      <SectionStart
        data-testid="sectionStart"
        ref={refStart}
        split={split}
        splitMedium={splitMedium}
        splitLarge={splitLarge}
        splitExtraLarge={splitExtraLarge}
      >
        {contentStart}
      </SectionStart>
      <SectionEnd
        data-testid="sectionEnd"
        ref={refEnd}
        split={split}
        splitMedium={splitMedium}
        splitLarge={splitLarge}
      >
        {contentEnd}
      </SectionEnd>
    </StyledRowSection>
  );
};

const ComponentSplitMap: {
  [split in SplitType]: {
    Start: string;
    End: string;
  };
} = {
  '50:50': {
    Start: '50%',
    End: '50%',
  },
  '65:35': {
    Start: '65%',
    End: '35%',
  },
  '60:40': {
    Start: '60%',
    End: '40%',
  },
  '60:25': {
    Start: '60%',
    End: '25%',
  },
  '45:25': {
    Start: '45%',
    End: '25%',
  },
  '20:80': {
    Start: '20%',
    End: '80%',
  },
  '30:70': {
    Start: '30%',
    End: '70%',
  },
  '15:85': {
    Start: '15%',
    End: '85%',
  },
};

export const SPLIT_VIEW_SECTION_PADDING = 'calc(2.7vw + 5px)';
export const SPLIT_VIEW_SECTION_PADDING_MIN_L = '45px';

const SectionStart = styled.div<ComponentSplit>`
  --width: ${({split}) =>
    split ? ComponentSplitMap[split].Start : ComponentSplitMap['50:50'].Start};

  --width-desktop-and-up: ${({splitMedium}) =>
    splitMedium ? ComponentSplitMap[splitMedium].Start : 'var(--width)'};

  --width-large-screens: ${({splitLarge}) =>
    splitLarge
      ? ComponentSplitMap[splitLarge].Start
      : 'var(--width-desktop-and-up)'};

  --width-extra-large-screens: ${({splitExtraLarge}) =>
    splitExtraLarge
      ? ComponentSplitMap[splitExtraLarge].Start
      : 'var(--width-large-screens)'};

  width: var(--width);

  @media ${mediaDesktopAndUp} {
    width: var(--width-desktop-and-up);
  }

  @media ${mediaLargeScreens} {
    width: var(--width-large-screens);
  }

  @media ${mediaExtraLargeScreens} {
    width: var(--width-extra-large-screens);
  }

  [data-disable-split='true'] & {
    width: 0;
  }

  [data-disable-split='false'] & {
    padding-right: ${SPLIT_VIEW_SECTION_PADDING};
    @media ${mediaMinL} {
      padding-right: ${SPLIT_VIEW_SECTION_PADDING_MIN_L};
    }
  }
`;

const SectionEnd = styled.div<ComponentSplit>`
  --width: ${({split}) =>
    split ? ComponentSplitMap[split].End : ComponentSplitMap['50:50'].End};

  --width-desktop-and-up: ${({splitMedium}) =>
    splitMedium ? ComponentSplitMap[splitMedium].End : 'var(--width)'};

  --width-large-screens: ${({splitLarge}) =>
    splitLarge
      ? ComponentSplitMap[splitLarge].End
      : 'var(--width-desktop-and-up)'};

  --width-extra-large-screens: ${({splitExtraLarge}) =>
    splitExtraLarge
      ? ComponentSplitMap[splitExtraLarge].End
      : 'var(--width-large-screens)'};

  width: var(--width);

  @media ${mediaDesktopAndUp} {
    width: var(--width-desktop-and-up);
  }

  @media ${mediaLargeScreens} {
    width: var(--width-large-screens);
  }

  @media ${mediaExtraLargeScreens} {
    width: var(--width-extra-large-screens);
  }

  [data-disable-split='true'] & {
    width: 100%;
  }

  [data-reduce-padding='false'][data-disable-split='false'] & {
    padding-left: ${SPLIT_VIEW_SECTION_PADDING};

    @media ${mediaMinL} {
      padding-left: ${SPLIT_VIEW_SECTION_PADDING_MIN_L};
    }
  }

  [data-has-fixed-spacing='true'] & {
    padding-left: 0;
  }
`;

const FIXED_SPLIT_VIEW_SECTION_PADDING = '60px';
const FIXED_SPLIT_VIEW_SECTION_PADDING_TABLET = '24px';

const StyledRowSection = styled(RowSection)<Partial<SplitViewProps>>`
  width: 100%;
  display: flex;
  box-sizing: border-box;

  &[data-has-fixed-spacing='true'] {
    gap: ${FIXED_SPLIT_VIEW_SECTION_PADDING_TABLET};

    @media ${mediaDesktopAndUp} {
      gap: ${FIXED_SPLIT_VIEW_SECTION_PADDING};
    }
  }
`;
